import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatErr } from "../../lib/api";
import { toast } from "sonner";
import { Search, X, Check, User, Building2, Calendar as CalendarIcon } from "@/components/icons";
import { useOfficeFilter } from "../../lib/officeFilter";
import SelectDateTimePicker from "../SelectDateTimePicker";
import AppleModal from "../AppleModal";

function parseApptDate(s) {
  if (!s || typeof s !== "string") return null;
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!m) return null;
  return new Date(
    parseInt(m[1]),
    parseInt(m[2]) - 1,
    parseInt(m[3]),
    parseInt(m[4]),
    parseInt(m[5])
  );
}

const initials = (name) =>
  (name || "")
    .split(" ")
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

export default function NewAppointmentModal({
  open,
  onClose,
  onCreated,
  prefillDate = "",
  prefillPatientId = "",
}) {
  const { officeId } = useOfficeFilter();
  const [patients, setPatients] = useState([]);
  const [offices, setOffices] = useState([]);
  const [appts, setAppts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  const [query, setQuery] = useState("");
  const [showResults, setShowResults] = useState(false);
  const [selected, setSelected] = useState(null);
  const wrapRef = useRef(null);

  const [form, setForm] = useState({
    date: prefillDate,
    duration_min: 30,
    reason: "",
  });

  // Reset on open
  useEffect(() => {
    if (open) {
      setSelected(null);
      setQuery("");
      setShowResults(false);
      setSuccess(false);
      setForm({ date: prefillDate, duration_min: 30, reason: "" });
      (async () => {
        try {
          const [p, o, a] = await Promise.all([
            api.get("/patients"),
            api.get("/offices").catch(() => ({ data: [] })),
            api.get("/appointments").catch(() => ({ data: [] })),
          ]);
          setPatients(p.data);
          setOffices(o.data);
          setAppts(a.data);
          if (prefillPatientId) {
            const match = (p.data || []).find((x) => x.id === prefillPatientId);
            if (match) {
              setSelected(match);
              setQuery(match.full_name || "");
            }
          }
        } catch (err) {
          toast.error(formatErr(err.response?.data?.detail));
        }
      })();
    }
  }, [open, prefillDate, prefillPatientId]);

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Close suggestions on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target))
        setShowResults(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const norm = (s) => (s || "").toString().toLowerCase().trim();
  const results = useMemo(() => {
    const q = norm(query);
    if (!q) return patients.slice(0, 8);
    return patients
      .filter((p) => {
        const name = norm(p.full_name);
        const phone = norm(p.phone);
        const doc = norm(p.document);
        return (
          name.includes(q) ||
          phone.replace(/\s|-/g, "").includes(q.replace(/\s|-/g, "")) ||
          doc.includes(q)
        );
      })
      .slice(0, 12);
  }, [query, patients]);

  const pickPatient = (p) => {
    setSelected(p);
    setQuery(p.full_name);
    setShowResults(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!selected) {
      toast.error("Selecciona un paciente");
      return;
    }
    if (!form.date) {
      toast.error("Selecciona fecha y hora");
      return;
    }
    setSaving(true);
    try {
      await api.post("/appointments", {
        patient_id: selected.id,
        office_id: officeId || null,
        date: form.date,
        duration_min: parseInt(form.duration_min) || 30,
        reason: form.reason,
        status: "scheduled",
      });
      setSuccess(true);
      // Espera 1.4s para que se vea la animación, luego cerrar y notificar
      setTimeout(() => {
        onCreated?.(form.date);
        onClose?.();
      }, 1400);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <AppleModal
      open
      onClose={saving ? undefined : onClose}
      icon={<CalendarIcon weight="regular" className="w-11 h-11" strokeWidth={1.5} />}
      title="Nueva cita"
      subtitle="Busca al paciente, elige fecha y motivo"
      size="lg"
      testid="new-appointment-modal"
    >
      <div className="relative">
        {/* Overlay de éxito */}
        {success && (
          <div
            className="absolute inset-0 z-30 bg-white flex flex-col items-center justify-center gap-3 rounded-[24px]"
            data-testid="appt-success-overlay"
          >
            <SuccessCheck />
            <div className="text-[15px] font-semibold text-black">
              Cita agendada
            </div>
            <div className="text-[12px] text-black/45">
              {selected?.full_name} · {form.date && formatShortDate(form.date)}
            </div>
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
            {/* Patient search */}
            <div>
              <label className="text-[10px] uppercase tracking-widest font-semibold text-black/45 flex items-center gap-1.5">
                <User className="w-3 h-3" strokeWidth={1.75} weight="regular" />
                Paciente
                {selected && (
                  <span className="ml-auto normal-case tracking-normal text-[10px] text-emerald-600 inline-flex items-center gap-1">
                    <Check className="w-3 h-3" strokeWidth={2} weight="regular" />
                    seleccionado
                  </span>
                )}
              </label>
              <div className="relative mt-1.5" ref={wrapRef}>
                <Search
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-black/40"
                  strokeWidth={1.75}
                  weight="regular"
                />
                <input
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setSelected(null);
                    setShowResults(true);
                  }}
                  onFocus={() => setShowResults(true)}
                  placeholder="Buscar por nombre, teléfono o documento…"
                  className="w-full h-10 pl-9 pr-3 rounded-lg border border-black/[0.08] bg-white text-[13px] focus:outline-none focus:border-black/40 transition"
                  data-testid="patient-search-input"
                  autoComplete="off"
                />

                {showResults && (
                  <div
                    className="absolute z-20 left-0 right-0 mt-1.5 max-h-64 overflow-auto rounded-xl bg-white border border-black/[0.08] shadow-lg"
                    data-testid="patient-search-results"
                  >
                    {results.length === 0 && (
                      <div className="p-3 text-[12px] text-black/50">
                        Sin resultados. Crea el paciente primero desde
                        "Pacientes".
                      </div>
                    )}
                    {results.map((p) => (
                      <button
                        type="button"
                        key={p.id}
                        onClick={() => pickPatient(p)}
                        className="w-full text-left px-3 py-2 hover:bg-black/[0.04] flex items-center gap-2.5 border-b border-black/[0.04] last:border-0 transition"
                        data-testid={`patient-option-${p.id}`}
                      >
                        <div className="w-8 h-8 rounded-full bg-black text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                          {initials(p.full_name)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold text-black text-[13px] truncate">
                            {p.full_name}
                          </div>
                          <div className="text-[11px] text-black/50 truncate">
                            {p.phone || "Sin teléfono"}
                            {p.document ? ` · ${p.document}` : ""}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {selected && (
                <div
                  className="mt-2 rounded-lg bg-[#F2F3F5] p-2.5 flex items-center gap-2.5"
                  data-testid="patient-selected-card"
                >
                  <div className="w-9 h-9 rounded-full bg-black text-white flex items-center justify-center text-xs font-bold">
                    {initials(selected.full_name)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-black text-[13px] truncate">
                      {selected.full_name}
                    </div>
                    <div className="text-[11px] text-black/55 truncate">
                      {selected.phone || "Sin teléfono"}
                      {selected.email ? ` · ${selected.email}` : ""}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setSelected(null);
                      setQuery("");
                    }}
                    className="text-[11px] font-semibold text-black/60 hover:text-black"
                  >
                    Cambiar
                  </button>
                </div>
              )}
            </div>

            {/* Office info */}
            <div
              className="flex items-center gap-2.5 rounded-lg bg-[#F2F3F5] p-2.5"
              data-testid="appt-active-office"
            >
              <div className="w-8 h-8 rounded-lg bg-black text-white flex items-center justify-center flex-shrink-0">
                <Building2 className="w-4 h-4" strokeWidth={1.75} weight="regular" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-widest font-semibold text-black/45">
                  Oficina
                </div>
                <div className="font-semibold text-black text-[13px] truncate">
                  {offices.find((o) => o.id === officeId)?.name ||
                    "Sin oficina · selecciona arriba"}
                </div>
              </div>
            </div>

            {/* Date + Duration + Reason */}
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="text-[10px] uppercase tracking-widest font-semibold text-black/45">
                  Fecha y hora
                </label>
                <div className="mt-1.5">
                  <SelectDateTimePicker
                    value={form.date}
                    onChange={(v) => setForm({ ...form, date: v })}
                    testid="appt-date"
                    busyRanges={appts
                      .filter((a) => a.status !== "cancelled")
                      .filter(
                        (a) =>
                          !officeId ||
                          !a.office_id ||
                          a.office_id === officeId
                      )
                      .map((a) => {
                        const start = parseApptDate(a.date);
                        if (!start) return null;
                        const end = new Date(
                          start.getTime() +
                            (a.duration_min || 30) * 60000
                        );
                        return { start, end };
                      })
                      .filter(Boolean)}
                  />
                </div>
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-widest font-semibold text-black/45">
                  Duración (min)
                </label>
                <input
                  type="number"
                  min="5"
                  step="5"
                  value={form.duration_min}
                  onChange={(e) =>
                    setForm({ ...form, duration_min: e.target.value })
                  }
                  className="w-full mt-1.5 h-10 rounded-lg border border-black/[0.08] bg-white px-3 text-[13px] focus:outline-none focus:border-black/40 transition"
                  data-testid="appt-duration-input"
                />
              </div>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-widest font-semibold text-black/45">
                Motivo de la consulta
              </label>
              <textarea
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                placeholder="Ej: Control general, dolor abdominal..."
                className="w-full mt-1.5 rounded-lg border border-black/[0.08] bg-white px-3 py-2 min-h-[64px] text-[13px] focus:outline-none focus:border-black/40 transition"
                data-testid="appt-reason-input"
              />
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="h-11 px-5 rounded-full bg-white border border-black/15 hover:bg-black/[0.03] text-[14px] font-medium text-black transition disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={saving}
                data-testid="appt-submit-button"
                className="h-11 px-6 rounded-full bg-[#0071E3] text-white hover:bg-[#0077ED] text-[14px] font-semibold transition disabled:opacity-40"
              >
                {saving ? "Guardando..." : "Agendar cita"}
              </button>
            </div>
          </form>
        </div>

      {/* Keyframes for check animation */}
      <style>{`
        @keyframes eme-circle-draw {
          0% { stroke-dashoffset: 200; }
          100% { stroke-dashoffset: 0; }
        }
        @keyframes eme-check-draw {
          0% { stroke-dashoffset: 60; }
          100% { stroke-dashoffset: 0; }
        }
        @keyframes eme-pop {
          0% { transform: scale(0.7); opacity: 0; }
          60% { transform: scale(1.08); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </AppleModal>
  );
}

function SuccessCheck() {
  return (
    <div
      className="w-24 h-24 rounded-full bg-emerald-50 flex items-center justify-center"
      style={{ animation: "eme-pop 400ms cubic-bezier(0.34,1.56,0.64,1) both" }}
    >
      <svg viewBox="0 0 60 60" className="w-16 h-16">
        <circle
          cx="30"
          cy="30"
          r="26"
          fill="none"
          stroke="#10B981"
          strokeWidth="3"
          strokeLinecap="round"
          style={{
            strokeDasharray: 200,
            strokeDashoffset: 200,
            animation: "eme-circle-draw 500ms ease-out 100ms forwards",
          }}
        />
        <path
          d="M18 31 L27 40 L43 22"
          fill="none"
          stroke="#10B981"
          strokeWidth="4"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            strokeDasharray: 60,
            strokeDashoffset: 60,
            animation: "eme-check-draw 350ms ease-out 550ms forwards",
          }}
        />
      </svg>
    </div>
  );
}

function formatShortDate(s) {
  const d = parseApptDate(s);
  if (!d) return "";
  return d.toLocaleDateString("es-ES", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}
